import { useEffect, useRef, type RefObject } from 'react';

type Inside = RefObject<HTMLElement | null> | readonly RefObject<HTMLElement | null>[];

/**
 * Closes a hand-placed floating panel on a press or a focus move outside it, as Radix's dismissable layers close.
 * For the panels the shared primitives do not place: the knowledge editor's `[[` picker at the text caret, the canvas
 * item switcher's list drawn at the body, and the app surfaces' small menus. Everything else uses Radix Popover or
 * the Menu kit. `inside` holds the panel with its trigger, so a press on the trigger is left to the trigger's own
 * toggle. Presses are read in the capture phase, before any handler below can stop them. A mouse or pen closes the
 * panel at once and a touch only on its click, so a scroll that starts outside the panel leaves it open. Moving these
 * panels onto Radix's own DismissableLayer, so Escape and nested layers reach the topmost one, is #3735.
 */
export function useOutsidePress(open: boolean, inside: Inside, close: () => void) {
  const latest = useRef({ inside, close });
  latest.current = { inside, close };
  useEffect(() => {
    if (!open) return;
    const outside = (event: Event) => {
      const target = event.target as Node | null;
      const refs = latest.current.inside;
      const list = 'current' in refs ? [refs] : refs;
      return Boolean(target && !list.some((ref) => ref.current?.contains(target)));
    };
    const shut = (event: Event) => {
      if (outside(event)) latest.current.close();
    };
    const press = (event: PointerEvent) => {
      if (!outside(event)) return;
      if (event.pointerType !== 'touch') return latest.current.close();
      // A touch that scrolled has no click; the next click decides, wherever it lands.
      document.removeEventListener('click', shut, true);
      document.addEventListener('click', shut, { capture: true, once: true });
    };
    document.addEventListener('pointerdown', press, true);
    document.addEventListener('focusin', shut);
    return () => {
      document.removeEventListener('pointerdown', press, true);
      document.removeEventListener('click', shut, true);
      document.removeEventListener('focusin', shut);
    };
  }, [open]);
}
